import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CircleCheck, QrCode } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getOrder, formatPrice } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function OrderConfirmation() {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        getOrder(orderId)
            .then(setOrder)
            .catch(() => setNotFound(true));
    }, [orderId]);

    if (notFound) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-40 pb-24 text-center">
                <h1 className="font-serif-display text-4xl">Order not found.</h1>
                <Link to="/shop" className="btn-luxe btn-green mt-8 inline-flex">Shop Collection</Link>
            </main>
        );
    }

    if (!order) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-36 pb-24 px-5">
                <div className="max-w-[640px] mx-auto space-y-5">
                    <Skeleton className="h-16 w-2/3 mx-auto bg-black/[0.06]" />
                    <Skeleton className="h-80 bg-black/[0.06] rounded-xl" />
                </div>
            </main>
        );
    }

    return (
        <main className="bg-[#F6F1E7] min-h-screen pt-28 sm:pt-36 pb-24" data-testid="order-confirmation-page">
            <div className="max-w-[680px] mx-auto px-5 sm:px-8">
                <Reveal className="text-center">
                    <CircleCheck size={44} strokeWidth={1.2} className="text-[#0F2A1F] mx-auto" />
                    <h1 className="font-serif-display text-4xl sm:text-5xl font-medium mt-6">
                        Thank you, {order.customer.first_name}.
                    </h1>
                    <p className="text-sm text-black/55 mt-4 leading-relaxed max-w-md mx-auto">
                        Your order is confirmed. From British fields, to your hands — it's on its way.
                    </p>
                    <div className="inline-block mt-8 border border-black/15 bg-white/50 rounded-lg px-8 py-4">
                        <p className="font-mono-meta text-[9px] tracking-[0.25em] text-black/40">ORDER NUMBER</p>
                        <p className="font-serif-display text-3xl font-medium mt-1" data-testid="order-confirmation-number">
                            {order.order_number}
                        </p>
                    </div>
                    <p className="font-mono-meta text-[9px] tracking-wide text-black/35 mt-4">
                        A CONFIRMATION HAS BEEN SENT TO {order.customer.email.toUpperCase()}
                    </p>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="bg-[#0B0B0C] text-[#F6F1E7] rounded-xl p-7 mt-12" data-testid="order-confirmation-summary">
                        <h2 className="font-serif-display text-xl font-medium mb-5">Your Order</h2>
                        <div className="space-y-4">
                            {order.items.map((item) => (
                                <div key={item.product_id} className="flex items-center justify-between gap-4 pb-4 border-b border-white/[0.07]">
                                    <div>
                                        <p className="font-serif-display text-base">{item.name}</p>
                                        <p className="font-mono-meta text-[9px] text-[#C9B37A]/70 mt-1">
                                            BATCH {item.batch_number} · QTY {item.quantity}
                                        </p>
                                    </div>
                                    <span className="text-sm text-[#C9B37A]">{formatPrice(item.line_total)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 space-y-2 text-sm text-white/60">
                            {order.savings > 0 && (
                                <div className="flex justify-between text-[#C9B37A]"><span>Multi-buy savings</span><span>−{formatPrice(order.savings)}</span></div>
                            )}
                            <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
                            <div className="flex justify-between"><span>Delivery</span><span>{order.shipping === 0 ? "Complimentary" : formatPrice(order.shipping)}</span></div>
                            <div className="flex justify-between items-baseline pt-3 border-t border-white/10">
                                <span className="eyebrow text-white/50">Total</span>
                                <span className="font-serif-display text-2xl text-[#F6F1E7]">{formatPrice(order.total)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 mt-7 bg-white/[0.05] border border-white/10 rounded-lg p-4">
                            <QrCode size={30} strokeWidth={1} className="text-[#C9B37A] flex-shrink-0" />
                            <p className="text-xs text-white/55 leading-relaxed">
                                Every pack in your order carries a batch number and QR code — scan it to meet the farm and
                                farmer behind your beef.
                            </p>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.25} className="text-center mt-10">
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/shop" className="btn-luxe btn-green" data-testid="confirmation-continue-shopping">
                            Continue Shopping
                        </Link>
                        <Link to="/farms" className="btn-luxe btn-outline-dark" data-testid="confirmation-meet-farmers">
                            Meet Your Farmers
                        </Link>
                    </div>
                </Reveal>
            </div>
        </main>
    );
}
